import { useContext, useState } from 'react';

import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Divider,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { context } from 'src/contexts/AppContext';

import PublicIcon from '@mui/icons-material/Public';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DescriptionIcon from '@mui/icons-material/Description';
import { Business as BusinessIcon, Settings } from '@mui/icons-material';
import { refreshBigQueryDataUsecase } from 'src/api/dashboard/usecases/refresh-big-query-data.usecase';
import { useSuspiciousUsersIndicator } from 'src/hooks/useSuspiciousUsersIndicator';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)} ${theme.spacing(0.5)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
    'transform',
    'opacity'
  ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

const DotIndicator = styled(Box)(
  ({ theme }) => `
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${theme.colors.error.main};
    margin-left: 7px;
    margin-top: -10px;
  `
);

function SidebarMenu() {
  const { closeSidebar } = useContext(context);
  const [isRefreshAlertOpen, setRefreshAlertOpen] = useState(false);
  const [isRefreshSuccess, setIsRefreshSuccess] = useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);

  const { data: newAlertsTimestamp } = useSuspiciousUsersIndicator();

  const theme = useTheme();

  const handleLookerRefresh = async () => {
    setIsRefreshLoading(true);

    try {
      await refreshBigQueryDataUsecase();
      setIsRefreshLoading(false);
      setIsRefreshSuccess(true);
      setRefreshAlertOpen(true);
    } catch {
      setIsRefreshSuccess(false);
      setRefreshAlertOpen(true);
    }
  };

  return (
    <>
      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              eSIMo
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {/*<ListItem component="div">*/}
              {/*  <Button*/}
              {/*      disableRipple*/}
              {/*      component={RouterLink}*/}
              {/*      onClick={closeSidebar}*/}
              {/*      to="/dashboards/dashboard"*/}
              {/*      startIcon={<AppsIcon />}*/}
              {/*  >*/}
              {/*    Dashboard*/}
              {/*  </Button>*/}
              {/*</ListItem>*/}
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/purchases"
                  startIcon={<TableChartTwoToneIcon />}
                >
                  Purchases
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/users"
                  startIcon={<PeopleAltIcon />}
                >
                  Users List
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/suspicious-users"
                  startIcon={<PeopleAltIcon />}
                >
                  Suspicious Users
                  {newAlertsTimestamp ? <DotIndicator /> : null}
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/businesses"
                  startIcon={<BusinessIcon />}
                >
                  Businesses
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/coupons"
                  startIcon={<RequestQuoteIcon />}
                >
                  Coupons
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/logs"
                  startIcon={<DescriptionIcon />}
                >
                  Esimoney Admin Logs
                </Button>
              </ListItem>

              <Divider
                sx={{
                  background: 'grey',
                  margin: 2
                }}
              />

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/packages"
                  startIcon={<BarChartIcon />}
                >
                  Packages
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/countries"
                  startIcon={<PublicIcon />}
                >
                  Countries
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/settings"
                  startIcon={<Settings />}
                >
                  Providers
                </Button>
              </ListItem>

              <Button
                disabled={isRefreshLoading}
                onClick={handleLookerRefresh}
                style={{ marginTop: '10px' }}
                variant="contained"
                fullWidth
                sx={{
                  ":disabled": {
                    background: theme.colors.primary.light
                  }
                }}
              >
                {isRefreshLoading ? 'Refreshing...' : 'Refresh Looker'}
              </Button>
            </List>
          </SubMenuWrapper>
        </List>
        <Snackbar
          open={isRefreshAlertOpen}
          autoHideDuration={4000}
          onClose={() => setRefreshAlertOpen(false)}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          <Alert
            onClose={() => setRefreshAlertOpen(false)}
            severity={isRefreshSuccess ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {isRefreshSuccess
              ? 'Looker refreshed successfully'
              : 'Cannot refresh looker data'}
          </Alert>
        </Snackbar>
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;